import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, Eye, AlertTriangle, Check, FileImage, ArrowLeft, Clock, MapPin, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { API } from '../App';

const SkinScanner = ({ user, setUser }) => {
  const [step, setStep] = useState('capture'); // capture, analyze, results
  const [capturedImage, setCapturedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [bodyPart, setBodyPart] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [duration, setDuration] = useState('');
  
  const fileInputRef = useRef();
  const videoRef = useRef();
  const canvasRef = useRef();

  const bodyParts = [
    'Face', 'Neck', 'Arms', 'Hands', 'Chest', 'Back', 'Legs', 'Feet', 'Scalp', 'Other'
  ];

  const durationOptions = [
    'Less than 1 week', '1-2 weeks', '2-4 weeks', '1-3 months', '3-6 months', 'More than 6 months'
  ];

  // Camera capture functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      setError('Unable to access camera. Please try uploading an image instead.');
    }
  };

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    if (!canvas || !video) {
      setError('Camera not ready. Please try again.');
      return;
    }

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      setError('Video not loaded. Please wait a moment and try again.');
      return;
    }

    try {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
      setError('');
      
      // Stop camera
      if (video.srcObject) {
        video.srcObject.getTracks().forEach(track => track.stop());
      }
    } catch (err) {
      setError('Failed to capture photo. Please try again.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Invalid file type:', file.type);
      setError('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      console.error('File too large:', file.size);
      setError('Image size must be less than 10MB');
      return;
    }

    process.env.NODE_ENV === 'development' && console.log('✓ File validation passed:', file.name, file.type, file.size);
    setImageFile(file);
    setError('');
    
    const reader = new FileReader();
    reader.onloadstart = () => {
      process.env.NODE_ENV === 'development' && console.log('FileReader: Starting to read file...');
    };
    reader.onprogress = (e) => {
      process.env.NODE_ENV === 'development' && console.log('FileReader: Progress', Math.round((e.loaded / e.total) * 100) + '%');
    };
    reader.onload = (e) => {
      const result = e.target.result;
      process.env.NODE_ENV === 'development' && console.log('✓ FileReader: Load complete, data length:', result.length);
      process.env.NODE_ENV === 'development' && console.log('✓ FileReader: Data preview:', result.substring(0, 50) + '...');
      process.env.NODE_ENV === 'development' && console.log('✓ Setting capturedImage state...');
      setCapturedImage(result);
      process.env.NODE_ENV === 'development' && console.log('✓ capturedImage state set');
    };
    reader.onerror = (e) => {
      console.error('✗ FileReader error:', e);
      setError('Failed to read image file');
    };
    
    process.env.NODE_ENV === 'development' && console.log('Starting FileReader.readAsDataURL...');
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!capturedImage) {
      setError('Please capture or upload an image first.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const requestData = {
        image_data: capturedImage,
        body_part: bodyPart || undefined,
        symptoms: symptoms || undefined,
        duration: duration || undefined
      };

      const response = await axios.post(`${API}/skin-scanner/analyze`, requestData);
      setAnalysisData(response.data);
      setStep('results');
    } catch (error) {
      console.error('Analysis error:', error);
      setError(error.response?.data?.detail || 'Failed to analyze image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setStep('capture');
    setCapturedImage(null);
    setImageFile(null);
    setAnalysisData(null);
    setBodyPart('');
    setSymptoms('');
    setDuration('');
    setError('');
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'mild': return 'text-green-600 bg-green-50';
      case 'moderate': return 'text-yellow-600 bg-yellow-50';
      case 'severe': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Capture Step
  if (step === 'capture') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        
        <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
          <div className="max-w-4xl mx-auto">
            
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">
                AI Skin Condition Scanner
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Get instant AI-powered analysis of skin conditions
              </p>
              
              {/* Medical Disclaimer */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <strong>Medical Disclaimer:</strong> This AI analysis is for informational purposes only. 
                    Always consult a qualified dermatologist for proper diagnosis and treatment.
                  </div>
                </div>
              </div>
            </div>

            {/* Image Capture Options */}
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Camera Capture */}
              <Card className="p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Take Photo</h3>
                  <p className="text-gray-600 mb-6">Use your device camera to capture the affected area</p>
                  
                  {!capturedImage ? (
                    <div>
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        className="w-full max-w-sm mx-auto rounded-lg mb-4 bg-black"
                        style={{ maxHeight: '400px' }}
                      />
                      <canvas ref={canvasRef} className="hidden" />
                      
                      <div className="space-y-3">
                        <Button 
                          onClick={startCamera}
                          data-testid="skin-scanner-start-camera"
                          variant={videoRef.current?.srcObject ? 'outline' : 'default'}
                          className="w-full"
                          disabled={!!videoRef.current?.srcObject}
                        >
                          Start Camera
                        </Button>
                        
                        {videoRef.current?.srcObject && (
                          <Button 
                            onClick={capturePhoto}
                            className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                          >
                            Capture Photo
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative bg-gray-50 p-4 rounded-lg">
                        <img 
                          src={capturedImage} 
                          alt="Captured skin area" 
                          className="w-full max-w-sm mx-auto rounded-lg border-2 border-gray-200 shadow-md block"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                          onLoad={() => {}}
                          onError={(e) => {
                            setError('Failed to display image. Please try again.');
                            setCapturedImage(null);
                          }}
                        />
                        <div className="absolute top-6 right-6 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                          Image Ready
                        </div>
                      </div>
                      <Button 
                        onClick={() => {
                          setCapturedImage(null);
                          setImageFile(null);
                          startCamera();
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Retake Photo
                      </Button>
                    </div>
                  )}
                </div>
              </Card>

              {/* File Upload */}
              <Card className="p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Upload Image</h3>
                  <p className="text-gray-600 mb-6">Select an existing photo from your device</p>
                  
                  {!capturedImage ? (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <Button 
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="skin-scanner-upload-btn"
                        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                      >
                        <FileImage className="w-4 h-4 mr-2" />
                        Choose Image
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative bg-gray-50 p-4 rounded-lg">
                        <img 
                          src={capturedImage} 
                          alt="Uploaded skin area" 
                          className="w-full max-w-sm mx-auto rounded-lg border-2 border-gray-200 shadow-md block"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                          onLoad={() => {}}
                          onError={(e) => {
                            setError('Failed to display image. Please try again.');
                            setCapturedImage(null);
                            setImageFile(null);
                          }}
                        />
                        <div className="absolute top-6 right-6 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                          Image Ready
                        </div>
                      </div>
                      <Button 
                        onClick={() => {
                          setCapturedImage(null);
                          setImageFile(null);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Choose Different Image
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Additional Information Form */}
            {capturedImage && (
              <Card className="mt-8 p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Additional Information (Optional)</h3>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Body Part
                    </label>
                    <select 
                      value={bodyPart} 
                      onChange={(e) => setBodyPart(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Select area...</option>
                      {bodyParts.map(part => (
                        <option key={part} value={part.toLowerCase()}>{part}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration
                    </label>
                    <select 
                      value={duration} 
                      onChange={(e) => setDuration(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">How long have you had this?</option>
                      {durationOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Symptoms
                    </label>
                    <input
                      type="text"
                      value={symptoms}
                      onChange={(e) => setSymptoms(e.target.value)}
                      placeholder="e.g., itchy, painful, dry..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}
                
                <div className="mt-6 flex gap-3">
                  <Button 
                    onClick={analyzeImage}
                    disabled={loading}
                    data-testid="skin-scanner-analyze-btn"
                    className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-700 hover:to-cyan-700 py-3"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Analyze Skin Condition
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    onClick={resetScanner}
                    variant="outline"
                    className="px-6"
                  >
                    Reset
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results Step
  if (step === 'results' && analysisData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        
        <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
          <div className="max-w-6xl mx-auto">
            
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button 
                onClick={() => setStep('capture')}
                variant="outline" 
                size="sm"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  Skin Analysis Results
                </h1>
                <p className="text-gray-600">AI-powered dermatological assessment</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              
              {/* Image and Details */}
              <div className="lg:col-span-1">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Analyzed Image</h3>
                  <img 
                    src={capturedImage} 
                    alt="Analyzed skin condition" 
                    className="w-full rounded-lg mb-4"
                  />
                  
                  {/* Analysis Details */}
                  <div className="space-y-3 text-sm">
                    {bodyPart && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">Area:</span>
                        <span className="capitalize">{bodyPart}</span>
                      </div>
                    )}
                    {duration && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">Duration:</span>
                        <span>{duration}</span>
                      </div>
                    )}
                    {symptoms && (
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">Symptoms:</span>
                        <span>{symptoms}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Confidence Score */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Analysis Confidence</span>
                      <span className={`text-sm font-semibold ${getConfidenceColor(analysisData.confidence_score)}`}>
                        {analysisData.confidence_score.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          analysisData.confidence_score >= 80 ? 'bg-green-500' :
                          analysisData.confidence_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${analysisData.confidence_score}%` }}
                      />
                    </div>
                  </div>
                </Card>
              </div>

              {/* Analysis Results */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Primary Assessment */}
                <Card className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Primary Assessment</h3>
                  
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-lg font-semibold text-teal-800">
                        {analysisData.primary_assessment.condition_name}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(analysisData.primary_assessment.severity)}`}>
                          {analysisData.primary_assessment.severity}
                        </span>
                        <span className="text-sm text-teal-600 font-medium">
                          {analysisData.primary_assessment.confidence_level.toFixed(1)}% confidence
                        </span>
                      </div>
                    </div>
                    <p className="text-teal-700 text-sm mb-3">
                      {analysisData.primary_assessment.description}
                    </p>
                    
                    {analysisData.primary_assessment.common_causes.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-teal-800">Common Causes: </span>
                        <span className="text-xs text-teal-700">
                          {analysisData.primary_assessment.common_causes.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Alternative Conditions */}
                {analysisData.alternative_conditions.length > 0 && (
                  <Card className="p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Alternative Possibilities</h3>
                    <div className="space-y-3">
                      {analysisData.alternative_conditions.map((condition, index) => (
                        <div key={`item-${index}`} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-800">{condition.condition_name}</h4>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(condition.severity)}`}>
                                {condition.severity}
                              </span>
                              <span className="text-sm text-gray-600">
                                {condition.confidence_level.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600">{condition.description}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Recommendations */}
                <div className="grid md:grid-cols-2 gap-6">
                  
                  {/* General Recommendations */}
                  {analysisData.general_recommendations.length > 0 && (
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        General Care
                      </h3>
                      <ul className="space-y-2">
                        {analysisData.general_recommendations.map((rec, index) => (
                          <li key={`item-${index}`} className="flex items-start gap-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}

                  {/* Home Care Tips */}
                  {analysisData.home_care_tips.length > 0 && (
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Check className="w-5 h-5 text-blue-600" />
                        Home Care
                      </h3>
                      <ul className="space-y-2">
                        {analysisData.home_care_tips.map((tip, index) => (
                          <li key={`item-${index}`} className="flex items-start gap-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}

                  {/* When to See Doctor */}
                  {analysisData.when_to_see_doctor.length > 0 && (
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        See a Doctor If
                      </h3>
                      <ul className="space-y-2">
                        {analysisData.when_to_see_doctor.map((condition, index) => (
                          <li key={`item-${index}`} className="flex items-start gap-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                            <span>{condition}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}

                  {/* Warning Signs */}
                  {analysisData.warning_signs.length > 0 && (
                    <Card className="p-6 border-red-200 bg-red-50">
                      <h3 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        Warning Signs
                      </h3>
                      <ul className="space-y-2">
                        {analysisData.warning_signs.map((sign, index) => (
                          <li key={`item-${index}`} className="flex items-start gap-2 text-sm text-red-700">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                            <span>{sign}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </div>

                {/* Medical Disclaimer */}
                <Card className="p-6 bg-yellow-50 border-yellow-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-800 mb-2">Important Medical Disclaimer</h4>
                      <p className="text-sm text-yellow-800">{analysisData.disclaimer}</p>
                    </div>
                  </div>
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    onClick={resetScanner}
                    className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-700 hover:to-cyan-700"
                  >
                    Scan Another Condition
                  </Button>
                  <Button 
                    onClick={() => window.print()}
                    variant="outline"
                    className="px-6"
                  >
                    Print Results
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SkinScanner;